import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { toast } from "react-toastify";
import API from "../../services/api";

// import API from "src/services/api";

// ─── CONFIG ───────────────────────────────────────────────────────────────────
const API_BASE       = `${process.env.REACT_APP_API_URL}/api/products`;
const CLOUD_NAME     = "dsi1mb2aj";
const UPLOAD_PRESET  = "mypreset";
const CLOUDINARY_URL = `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`;

const TOAST_CFG = {
  position: "top-center",
  autoClose: 3000,
  hideProgressBar: true,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  theme: "light",                               // ← was "light"
};

// ─── GLOBAL CSS ───────────────────────────────────────────────────────────────
const GLOBAL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap');
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
body { background: #f4f4fa; color: #1a1a2e; font-family: 'Poppins', sans-serif; }
::-webkit-scrollbar { width: 4px; height: 4px; }
::-webkit-scrollbar-track { background: #e8e8f0; }
::-webkit-scrollbar-thumb { background: #6e6e94; border-radius: 2px; }
@keyframes fadeUp  { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
@keyframes fadeIn  { from { opacity:0; } to { opacity:1; } }
@keyframes scaleIn { from { opacity:0; transform:scale(0.96) translateY(10px); } to { opacity:1; transform:scale(1) translateY(0); } }
@keyframes spin    { to { transform: rotate(360deg); } }
@keyframes shimmer { 0%,100%{opacity:.35} 50%{opacity:.65} }
@keyframes pulse   { 0%,100%{opacity:1} 50%{opacity:.5} }
.anim-fade-up  { animation: fadeUp  .3s ease both; }
.anim-fade-in  { animation: fadeIn  .2s ease both; }
.anim-scale-in { animation: scaleIn .25s cubic-bezier(.16,1,.3,1) both; }
.spin          { animation: spin 0.75s linear infinite; }
.pulse-anim    { animation: pulse 1.5s ease-in-out infinite; }
.row-enter     { animation: fadeUp .25s ease both; }
`;

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────
const RAJ  = { fontFamily: "'Poppins',sans-serif" };
const MONO = { fontFamily: "'Poppins', sans-serif" };
const SANS = { fontFamily: "'Poppins',sans-serif" };

// light theme palette → Light theme palette
// #ffffff → #ffffff   (deepest bg → pure white)
// #f8f8ff → #f8f8ff   (surface    → off-white)
// #eeeef6 → #eeeef6   (raised     → light lavender-gray)
// #d0d0e8 → #d0d0e8   (border     → light border)
// #b8b8d4 → #b8b8d4   (mid border → soft purple-gray)
// #4a4a75 → #4a4a75   (muted text → medium muted)
// #1a1a2e → #1a1a2e   (body text  → near-black)

const INPUT_CLS =
  "w-full bg-[#ffffff] border border-[#d0d0e8] rounded-[6px] px-[11px] py-[9px] " +
  "text-[#1a1a2e] text-[12px] outline-none transition-all duration-150 placeholder-[#6c6c96] " +
  "focus:border-[#7c6dfa] focus:shadow-[0_0_0_2px_rgba(124,109,250,0.13)] " +
  "disabled:opacity-40 disabled:cursor-not-allowed";

const INPUT_ERR =
  "border-[#f05060] focus:border-[#f05060] focus:shadow-[0_0_0_2px_rgba(240,80,96,0.13)]";

const EMPTY_FORM = {
  brand: "",
  itemCode: "",
  profileCode: "",
  sectionName: "",
  item: "",
  hsnCode: "72255010",
  uom: "KGS",
  profileThickness: [{ thkn: "", weightPerMeter: "" }],
  lengthDefault: "5.8",
  sectionImage: "",
};

// ─── CLOUDINARY UPLOAD ────────────────────────────────────────────────────────
async function uploadToCloudinary(file) {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("upload_preset", UPLOAD_PRESET);
  fd.append("cloud_name", CLOUD_NAME);
  const res  = await fetch(CLOUDINARY_URL, { method: "POST", body: fd });
  const data = await res.json();
  if (!data.secure_url) throw new Error(data.error?.message || "Upload failed");
  return data.secure_url;
}

// ─── SECTION LABEL ────────────────────────────────────────────────────────────
function SectionLabel({ children, divider = false }) {
  return (
    <p
      className={`text-[9.5px] tracking-[1.5px] uppercase text-[#4a4a75] mb-3 ${divider ? "border-t border-[#d0d0e8] pt-5" : ""}`}
      style={MONO}
    >
      {children}
    </p>
  );
}

// ─── FIELD LABEL ─────────────────────────────────────────────────────────────
function FieldLabel({ label, required, hint }) {
  return (
    <label className="text-[9.5px] tracking-[1.5px] uppercase text-[#4a4a75]" style={MONO}>
      {label}
      {required && <span className="text-[#f0c040] ml-[2px]">*</span>}
      {hint     && <span className="text-[#40d098] ml-1 text-[9px]">{hint}</span>}
    </label>
  );
}

// ─── IMAGE UPLOAD FIELD ───────────────────────────────────────────────────────
function ImageUploadField({ value, onChange, error }) {
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileRef = useRef(null);

  const processFile = async (file) => {
    if (!file || !file.type.startsWith("image/")) {
      toast.error("Only image files are accepted.", TOAST_CFG);
      return;
    }
    const previewUrl = URL.createObjectURL(file);
    onChange(previewUrl);
    setUploading(true);
    try {
      const url = await uploadToCloudinary(file);
      onChange(url);
      toast.success("Image uploaded successfully!", TOAST_CFG);
    } catch (err) {
      console.error("Error uploading image:", err);
      onChange("");
      toast.error("Failed to upload image.", TOAST_CFG);
    } finally {
      setUploading(false);
    }
  };

  const handleFile    = (e) => processFile(e.target.files[0]);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!uploading) setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // only clear when leaving the drop zone entirely
    if (!e.currentTarget.contains(e.relatedTarget)) setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (uploading) return;
    processFile(e.dataTransfer.files[0]);
  };

  const handlePaste = (e) => {
    if (uploading) return;
    const imageItem = Array.from(e.clipboardData?.items || []).find(
      (item) => item.type.startsWith("image/")
    );
    if (imageItem) processFile(imageItem.getAsFile());
  };

  const borderCls = error
    ? "border-[#f05060]"
    : isDragging
    ? "border-[#7c6dfa]"
    : value && !uploading
    ? "border-[rgba(64,208,152,0.5)]"
    : "border-[#d0d0e8]";

  const bgCls = isDragging ? "bg-[rgba(124,109,250,0.04)]" : "bg-[#ffffff]";

  return (
    <div className="flex flex-col gap-[5px]">
      <FieldLabel label="Section Image" required />
      <div
        tabIndex={0}
        className={`relative ${bgCls} border-2 border-dashed ${borderCls} rounded-[8px] transition-all cursor-pointer group
          hover:border-[#7c6dfa] outline-none focus:border-[#7c6dfa] focus:shadow-[0_0_0_2px_rgba(124,109,250,0.1)]`}
        onClick={() => !uploading && fileRef.current?.click()}
        onDragOver={handleDragOver}
        onDragEnter={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onPaste={handlePaste}
      >
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />

        {uploading ? (
          <div className="flex items-center justify-center gap-3 py-5 px-4">
            <span className="spin w-4 h-4 border-2 border-[#7c6dfa]/30 border-t-[#7c6dfa] rounded-full inline-block" />
            <span className="text-[12px] text-[#7c6dfa] pulse-anim" style={MONO}>Uploading to Cloudinary…</span>
          </div>
        ) : isDragging ? (
          <div className="flex flex-col items-center justify-center py-8 gap-2 pointer-events-none">
            <div className="text-[32px]">📥</div>
            <p className="text-[13px] font-semibold text-[#7c6dfa]" style={MONO}>Drop image here</p>
          </div>
        ) : value ? (
          <div className="flex items-center gap-4 p-3">
            <img
              src={value}
              alt="section"
              className="w-14 h-14 rounded-[6px] border border-[#d0d0e8] object-cover bg-[#eeeef6] flex-shrink-0"
              onError={e => { e.target.src = "https://placehold.co/56x56/eeeef6/999?text=?"; }}
            />
            <div className="flex-1 min-w-0">
              <p className="text-[11px] text-[#40d098] font-medium mb-[2px]" style={MONO}>Uploaded ✓</p>
              <p className="text-[10px] text-[#4a4a75] truncate" style={MONO}>{value}</p>
            </div>
            <span className="text-[10px] text-[#7c6dfa] font-semibold group-hover:underline flex-shrink-0" style={RAJ}>Change</span>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-6 gap-[6px]">
            <div className="text-[24px] opacity-30">🖼</div>
            <p className="text-[12px] text-[#4a4a75]" style={MONO}>
              Click, drag & drop, or{" "}
              <span className="text-[#7c6dfa]">paste</span> an image
            </p>
            <p className="text-[10px] text-[#4a4a75] opacity-60" style={MONO}>
              JPG · PNG · WebP — focus zone then Ctrl+V to paste
            </p>
          </div>
        )}
      </div>
      {error && <span className="text-[10px] text-[#f05060]" style={MONO}>{error}</span>}
    </div>
  );
}

// ─── PROFILE THICKNESS EDITOR ─────────────────────────────────────────────────
function ProfileThicknessEditor({ rows, onChange, rowErrors }) {
  const addRow    = () => onChange([...rows, { thkn: "", weightPerMeter: "" }]);
  const removeRow = (i) => onChange(rows.filter((_, idx) => idx !== i));
  const updateRow = (i, field, val) =>
    onChange(rows.map((r, idx) => (idx === i ? { ...r, [field]: val } : r)));

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div></div>
        <button
          type="button"
          onClick={addRow}
          className="inline-flex items-center gap-1 px-3 py-[4px] rounded-[5px] bg-[rgba(124,109,250,0.1)] border border-[rgba(124,109,250,0.3)] text-[#7c6dfa] text-[10px] font-bold hover:bg-[rgba(124,109,250,0.2)] transition-all"
          style={RAJ}
        >+ Add Row</button>
      </div>

      {/* Column headers */}
      <div className="grid grid-cols-[1fr_1fr_32px] gap-2 px-1">
        <span className="text-[9px] tracking-[1.2px] uppercase text-[#4a4a75]" style={MONO}>THKN (MM)</span>
        <span className="text-[9px] tracking-[1.2px] uppercase text-[#4a4a75]" style={MONO}>Weight / Meter (kg)</span>
        <span />
      </div>

      {rows.map((row, i) => (
        <div
          key={i}
          className="grid grid-cols-[1fr_1fr_32px] gap-2 items-start bg-[rgba(124,109,250,0.05)] border border-[#d0d0e8] rounded-[7px] p-2"
        >
          <div className="flex flex-col gap-1">
            <input
              className={`${INPUT_CLS} py-[7px] ${rowErrors?.[i]?.thkn ? INPUT_ERR : ""}`}
              style={MONO}
              type="number"
              step="any"
              value={row.thkn}
              onChange={e => updateRow(i, "thkn", e.target.value)}
              placeholder="e.g. 1.0"
            />
            {rowErrors?.[i]?.thkn && (
              <span className="text-[10px] text-[#f05060]" style={MONO}>{rowErrors[i].thkn}</span>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <input
              className={`${INPUT_CLS} py-[7px] ${rowErrors?.[i]?.weightPerMeter ? INPUT_ERR : ""}`}
              style={MONO}
              type="number"
              step="any"
              value={row.weightPerMeter}
              onChange={e => updateRow(i, "weightPerMeter", e.target.value)}
              placeholder="e.g. 0.4897"
            />
            {rowErrors?.[i]?.weightPerMeter && (
              <span className="text-[10px] text-[#f05060]" style={MONO}>{rowErrors[i].weightPerMeter}</span>
            )}
          </div>

          <button
            type="button"
            onClick={() => removeRow(i)}
            disabled={rows.length === 1}
            className="w-8 h-8 mt-[1px] flex items-center justify-center rounded-[6px] border border-[rgba(240,80,96,0.3)] text-[#f05060] text-[16px] hover:bg-[rgba(240,80,96,0.1)] transition-all disabled:opacity-20 disabled:cursor-not-allowed"
          >×</button>
        </div>
      ))}
    </div>
  );
}

// ─── CONFIRM DIALOG ───────────────────────────────────────────────────────────
function ConfirmDialog({ open, productName, onConfirm, onCancel }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[800] flex items-center justify-center anim-fade-in" onClick={onCancel}>
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" />
      <div
        className="relative anim-scale-in bg-[#f8f8ff] border border-[#b8b8d4] rounded-xl p-7 w-[340px] shadow-[0_24px_60px_rgba(0,0,0,.18)]"
        onClick={e => e.stopPropagation()}
      >
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#f05060] to-[#7c6dfa] rounded-t-xl" />
        <div className="text-[28px] mb-3 text-center">⚠️</div>
        <h3 className="text-[16px] font-bold text-[#1a1a2e] text-center mb-1" style={RAJ}>Delete Product?</h3>
        <p className="text-[12px] text-[#4a4a75] text-center mb-6" style={MONO}>
          <span className="text-[#7c6dfa]">{productName}</span> will be permanently removed.
        </p>
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 py-[9px] rounded-[7px] border border-[#b8b8d4] text-[#1a1a2e] text-[13px] font-semibold hover:border-[#7c6dfa] transition-all"
            style={RAJ}
          >Cancel</button>
          <button
            onClick={onConfirm}
            className="flex-1 py-[9px] rounded-[7px] bg-[#f05060] text-white text-[13px] font-semibold hover:bg-[#f07080] hover:-translate-y-px transition-all"
            style={RAJ}
          >Delete</button>
        </div>
      </div>
    </div>
  );
}

// ─── THICKNESS BADGE STACK ────────────────────────────────────────────────────
function ThicknessBadges({ variants = [] }) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? variants : variants.slice(0, 2);
  const extra   = variants.length - 2;
  return (
    <div className="flex flex-col gap-[3px] min-w-[130px]">
      {visible.map((v, i) => (
        <div
          key={i}
          className="flex items-center gap-[6px] bg-[rgba(124,109,250,0.07)] border border-[rgba(124,109,250,0.2)] rounded-[4px] px-[7px] py-[2px] w-fit"
        >
          <span className="text-[10px] text-[#7c6dfa]" style={MONO}>{v.thkn}mm</span>
          <span className="w-px h-3 bg-[#d0d0e8]" />
          <span className="text-[10px] text-[#40d098]" style={MONO}>{v.weightPerMeter}kg/m</span>
        </div>
      ))}
      {!expanded && extra > 0 && (
        <button
          onClick={() => setExpanded(true)}
          className="text-[9px] text-[#4a4a75] hover:text-[#7c6dfa] text-left transition-colors"
          style={MONO}
        >+{extra} more…</button>
      )}
    </div>
  );
}

// ─── SKELETON ROW ─────────────────────────────────────────────────────────────
function SkeletonRow() {
  return (
    <tr className="border-b border-[rgba(208,208,232,0.7)]">
      {[30, 40, 80, 90, 90, 130, 80, 60, 40, 110, 55, 80].map((w, i) => (
        <td key={i} className="px-3 py-[11px]">
          <div
            className="rounded-[4px] bg-[#eeeef6] h-3"
            style={{ width: w, animation: `shimmer 1.6s ease-in-out ${i * 0.07}s infinite` }}
          />
        </td>
      ))}
    </tr>
  );
}

// ─── PRODUCT MODAL ────────────────────────────────────────────────────────────
function ProductModal({ open, mode, initial, onClose, onSave, saving }) {
  const [form, setForm]           = useState(EMPTY_FORM);
  const [errors, setErrors]       = useState({});
  const [rowErrors, setRowErrors] = useState([]);

  useEffect(() => {
    if (!open) return;
    if (mode === "edit" && initial) {
      setForm({
        brand:       initial.brand        ?? "",
        itemCode:    initial.itemCode     ?? "",
        profileCode: initial.profileCode  ?? "",
        sectionName: initial.sectionName  ?? "",
        item:        initial.item         ?? "",
        hsnCode:     initial.hsnCode      ?? "72255010",
        uom:         initial.uom          ?? "KGS",
        profileThickness: initial.profileThickness?.length
          ? initial.profileThickness.map(t => ({
              thkn:           String(t.thkn),
              weightPerMeter: String(t.weightPerMeter),
            }))
          : [{ thkn: "", weightPerMeter: "" }],
        lengthDefault: String(initial.lengthDefault ?? "5.8"),
        sectionImage:  initial.sectionImage ?? "",
      });
    } else {
      setForm({ ...EMPTY_FORM, profileThickness: [{ thkn: "", weightPerMeter: "" }] });
    }
    setErrors({});
    setRowErrors([]);
  }, [open, mode, initial]);

  useEffect(() => {
    if (!open) return;
    const fn = e => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [open, onClose]);

  const setField = (field) => (val) => {
    setForm(prev => ({ ...prev, [field]: val }));
    setErrors(prev => ({ ...prev, [field]: "" }));
  };

  const validate = () => {
    const errs = {};
    const requiredStr = ["brand", "itemCode", "profileCode", "sectionName", "item", "hsnCode", "sectionImage"];
    requiredStr.forEach(k => {
      if (!form[k]?.toString().trim()) errs[k] = "Required";
    });
    if (!form.lengthDefault || isNaN(Number(form.lengthDefault)))
      errs.lengthDefault = "Valid number required";

    const rErrs = form.profileThickness.map(row => {
      const e = {};
      if (!row.thkn?.toString().trim() || isNaN(Number(row.thkn)))
        e.thkn = "Required";
      if (!row.weightPerMeter?.toString().trim() || isNaN(Number(row.weightPerMeter)))
        e.weightPerMeter = "Required";
      return Object.keys(e).length ? e : null;
    });

    if (rErrs.some(Boolean)) errs._thickness = true;

    setErrors(errs);
    setRowErrors(rErrs);
    return !Object.keys(errs).length;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    onSave({
      ...form,
      lengthDefault:    Number(form.lengthDefault),
      profileThickness: form.profileThickness.map(t => ({
        thkn:           Number(t.thkn),
        weightPerMeter: Number(t.weightPerMeter),
      })),
    });
  };

  if (!open) return null;

  const inp = (field, placeholder = "", type = "text", extra = {}) => (
    <input
      className={`${INPUT_CLS} ${errors[field] ? INPUT_ERR : ""}`}
      style={MONO}
      type={type}
      step={type === "number" ? "any" : undefined}
      value={form[field]}
      placeholder={placeholder}
      disabled={field === "itemCode" && mode === "edit"}
      onChange={e => setField(field)(e.target.value)}
      {...extra}
    />
  );

  return (
    <div className="fixed inset-0 z-[700] flex items-center justify-center anim-fade-in" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" />
      <div
        className="relative anim-scale-in bg-[#f8f8ff] border border-[#d0d0e8] rounded-xl w-[760px] max-w-[96vw] max-h-[92vh] flex flex-col shadow-[0_32px_80px_rgba(0,0,0,.16)] overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Accent top bar */}
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#7c6dfa] via-[#40c0f0] to-[#f0c040]" />

        {/* ── Header ── */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#d0d0e8] flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 rounded-full bg-[#7c6dfa] shadow-[0_0_8px_#7c6dfa]" />
            <span className="text-[13px] font-bold tracking-[3px] uppercase text-[#4a4a75]" style={RAJ}>
              {mode === "edit" ? "Edit Product" : "Add New Product"}
            </span>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-full border border-[#b8b8d4] text-[#4a4a75] hover:border-[#f05060] hover:text-[#f05060] transition-all text-[14px]"
          >✕</button>
        </div>

        {/* ── Scrollable body ── */}
        <div className="overflow-y-auto flex-1 px-6 py-5 flex flex-col gap-6">

          {/* Identification */}
          <div>
            <SectionLabel>Identification</SectionLabel>
            <div className="grid grid-cols-2 gap-3">

              <div className="col-span-2 flex flex-col gap-[5px]">
                <FieldLabel label="Brand" required />
                {inp("brand", "e.g. OKO CRAFT")}
                {errors.brand && <span className="text-[10px] text-[#f05060]" style={MONO}>{errors.brand}</span>}
              </div>

              <div className="flex flex-col gap-[5px]">
                <FieldLabel label="Item Code" required />
                {inp("itemCode", "OKC2010-3")}
                {errors.itemCode && <span className="text-[10px] text-[#f05060]" style={MONO}>{errors.itemCode}</span>}
              </div>

              <div className="flex flex-col gap-[5px]">
                <FieldLabel label="Profile Code" required />
                {inp("profileCode", "LS-C-60-02")}
                {errors.profileCode && <span className="text-[10px] text-[#f05060]" style={MONO}>{errors.profileCode}</span>}
              </div>

              <div className="col-span-2 flex flex-col gap-[5px]">
                <FieldLabel label="Section Name" required />
                {inp("sectionName", "3 TRACK")}
                {errors.sectionName && <span className="text-[10px] text-[#f05060]" style={MONO}>{errors.sectionName}</span>}
              </div>

              <div className="col-span-2 flex flex-col gap-[5px]">
                <FieldLabel label="Item / Dimensions" required />
                {inp("item", "17 X 32 X 17")}
                {errors.item && <span className="text-[10px] text-[#f05060]" style={MONO}>{errors.item}</span>}
              </div>
            </div>
          </div>

          {/* Compliance & Unit */}
          <div>
            <SectionLabel divider>Compliance & Unit</SectionLabel>
            <div className="grid grid-cols-3 gap-3">

              <div className="flex flex-col gap-[5px]">
                <FieldLabel label="HSN Code" required />
                {inp("hsnCode", "72255010")}
                {errors.hsnCode && <span className="text-[10px] text-[#f05060]" style={MONO}>{errors.hsnCode}</span>}
              </div>

              <div className="flex flex-col gap-[5px]">
                <FieldLabel label="UOM" required />
                <select
                  className={INPUT_CLS}
                  style={{ ...MONO, cursor: "pointer" }}
                  value={form.uom}
                  onChange={e => setField("uom")(e.target.value)}
                >
                  <option value="KGS">KGS</option>
                  <option value="MTR">MTR</option>
                  <option value="NOS">NOS</option>
                </select>
              </div>

              <div className="flex flex-col gap-[5px]">
                <FieldLabel label="Length Default (m)" required hint="● numeric" />
                {inp("lengthDefault", "5.8", "number")}
                {errors.lengthDefault && <span className="text-[10px] text-[#f05060]" style={MONO}>{errors.lengthDefault}</span>}
              </div>

            </div>
          </div>

          {/* Profile Thickness */}
          <div>
            <SectionLabel divider>Profile Thickness Variants Details</SectionLabel>
            <ProfileThicknessEditor
              rows={form.profileThickness}
              onChange={val => {
                setForm(prev => ({ ...prev, profileThickness: val }));
                setRowErrors([]);
                setErrors(prev => ({ ...prev, _thickness: undefined }));
              }}
              rowErrors={rowErrors}
            />
          </div>

          {/* Section Image */}
          <div>
            <SectionLabel divider>Section Drawing</SectionLabel>
            <ImageUploadField
              value={form.sectionImage}
              onChange={url => setField("sectionImage")(url)}
              error={errors.sectionImage}
            />
          </div>

        </div>

        {/* ── Footer ── */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-[#d0d0e8] bg-[rgba(240,240,252,0.9)] flex-shrink-0">
          <button
            onClick={onClose}
            className="px-5 py-[9px] rounded-[7px] border border-[#b8b8d4] text-[#1a1a2e] text-[13px] font-semibold hover:border-[#7c6dfa] transition-all"
            style={RAJ}
          >Cancel</button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="inline-flex items-center gap-2 px-6 py-[9px] rounded-[7px] bg-[#7c6dfa] text-white text-[13px] font-semibold hover:bg-[#9080ff] hover:-translate-y-px transition-all shadow-[0_4px_14px_rgba(124,109,250,0.35)] disabled:opacity-60 disabled:cursor-not-allowed disabled:translate-y-0"
            style={RAJ}
          >
            {saving && (
              <span className="spin w-3 h-3 border-2 border-white/30 border-t-white rounded-full inline-block" />
            )}
            {mode === "edit" ? "Save Changes" : "Add Product"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── CACHE UTILITIES ──────────────────────────────────────────────────────────
const CACHE_KEY = "rr_products_v1";
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { ts, data } = JSON.parse(raw);
    if (Date.now() - ts < CACHE_TTL) return { data, ts };
    return null;
  } catch { return null; }
}

function writeCache(data) {
  try {
    const ts = Date.now();
    localStorage.setItem(CACHE_KEY, JSON.stringify({ ts, data }));
    return ts;
  } catch { return null; }
}

function bustCache() {
  try { localStorage.removeItem(CACHE_KEY); } catch {}
}

// ─── EXPORT CSV ───────────────────────────────────────────────────────────────
function exportCSV(products) {
  const headers = ["#", "Brand", "Item Code", "Profile Code", "Section Name", "Item", "HSN", "UOM", "Length Default (m)", "Thickness Variants"];
  const rows = products.map((p, i) => [
    i + 1,
    p.brand || "",
    p.itemCode || "",
    p.profileCode || "",
    p.sectionName || "",
    p.item || "",
    p.hsnCode || "",
    p.uom || "",
    p.lengthDefault || "",
    (p.profileThickness || []).map(t => `${t.thkn}mm @ ${t.weightPerMeter}kg/m`).join("; "),
  ]);
  const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
  const csv = [headers, ...rows].map(r => r.map(esc).join(",")).join("\n");
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `products_${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ─── SORTABLE HEADER ──────────────────────────────────────────────────────────
function SortableHeader({ label, col, sortBy, sortDir, onSort, align = "left" }) {
  const active = sortBy === col;
  return (
    <th
      onClick={() => onSort(col)}
      className={`px-3 py-[11px] text-[9px] tracking-[1.2px] uppercase font-medium whitespace-nowrap
        cursor-pointer select-none transition-colors text-${align}
        ${active ? "text-[#7c6dfa]" : "text-[#4a4a75] hover:text-[#7c6dfa]"}`}
      style={MONO}
    >
      {label}
      <span className={`ml-[3px] text-[10px] ${active ? "opacity-100" : "opacity-20"}`}>
        {active ? (sortDir === "asc" ? "↑" : "↓") : "↕"}
      </span>
    </th>
  );
}

// ─── PAGINATION ───────────────────────────────────────────────────────────────
function Pagination({ page, pages, total, perPage, onPage }) {
  if (pages <= 1) return null;
  const from = (page - 1) * perPage + 1;
  const to   = Math.min(page * perPage, total);

  const getPages = () => {
    if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
    const arr = [1];
    if (page > 3) arr.push("…");
    for (let i = Math.max(2, page - 1); i <= Math.min(pages - 1, page + 1); i++) arr.push(i);
    if (page < pages - 2) arr.push("…");
    arr.push(pages);
    return arr;
  };

  const btnCls = (active) =>
    `w-8 h-8 flex items-center justify-center rounded-[7px] text-[12px] font-semibold
     transition-all cursor-pointer border
     ${active
       ? "bg-[#7c6dfa] text-white border-[#7c6dfa] shadow-[0_2px_8px_rgba(124,109,250,0.3)]"
       : "bg-[#ffffff] text-[#4a4a75] border-[#d0d0e8] hover:border-[#7c6dfa] hover:text-[#7c6dfa]"}`;

  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-[#d0d0e8] bg-[rgba(240,240,252,0.6)]">
      <span className="text-[11px] text-[#4a4a75]" style={MONO}>
        Showing <span className="text-[#1a1a2e] font-semibold">{from}–{to}</span> of{" "}
        <span className="text-[#1a1a2e] font-semibold">{total}</span> products
      </span>
      <div className="flex items-center gap-[4px]">
        <button onClick={() => onPage(page - 1)} disabled={page === 1}
          className={`${btnCls(false)} disabled:opacity-30 disabled:cursor-not-allowed`} style={MONO}>‹</button>
        {getPages().map((p, i) =>
          p === "…"
            ? <span key={`e${i}`} className="w-8 h-8 flex items-center justify-center text-[#4a4a75] text-[12px]" style={MONO}>…</span>
            : <button key={p} onClick={() => onPage(p)} className={btnCls(p === page)} style={MONO}>{p}</button>
        )}
        <button onClick={() => onPage(page + 1)} disabled={page === pages}
          className={`${btnCls(false)} disabled:opacity-30 disabled:cursor-not-allowed`} style={MONO}>›</button>
      </div>
    </div>
  );
}

// ─── GROUP BY OPTIONS ─────────────────────────────────────────────────────────
const GROUP_OPTIONS = [
  { label: "None",         val: ""            },
  { label: "Brand",        val: "brand"       },
  { label: "Section Name", val: "sectionName" },
  { label: "UOM",          val: "uom"         },
  { label: "HSN Code",     val: "hsnCode"     },
];

const PAGE_SIZE = 15;

const SORT_COLS = [
  { h: "#",                  col: null,             a: "center" },
  { h: "Drawing",            col: null,             a: "center" },
  { h: "Brand",              col: "brand",          a: "left"   },
  { h: "Item Code",          col: "itemCode",       a: "left"   },
  { h: "Profile Code",       col: "profileCode",    a: "left"   },
  { h: "Section Name",       col: "sectionName",    a: "left"   },
  { h: "Item",               col: "item",           a: "left"   },
  { h: "HSN",                col: "hsnCode",        a: "left"   },
  { h: "UOM",                col: "uom",            a: "center" },
  { h: "Thickness Variants", col: null,             a: "left"   },
  { h: "L.Def (m)",          col: "lengthDefault",  a: "right"  },
  { h: "Actions",            col: null,             a: "center" },
];

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function ProductsManager() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [cacheTs, setCacheTs]   = useState(null);

  const [search, setSearch]   = useState("");
  const [sortBy, setSortBy]   = useState("brand");
  const [sortDir, setSortDir] = useState("asc");
  const [groupBy, setGroupBy] = useState("");
  const [page, setPage]       = useState(1);

  const [modalOpen, setModalOpen]   = useState(false);
  const [modalMode, setModalMode]   = useState("add");
  const [editTarget, setEditTarget] = useState(null);

  const [confirmOpen, setConfirmOpen]   = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // ── Cache-aware fetch ──────────────────────────────────────────────────────
  const fetchProducts = useCallback(async (force = false) => {
    if (!force) {
      const cached = readCache();
      if (cached) {
        setProducts(cached.data);
        setCacheTs(cached.ts);
        setLoading(false);
        return;
      }
    }
    setLoading(true);
    try {
      const response = await API.get(API_BASE);
      const data = response.data.data || [];
      setProducts(data);
      setCacheTs(writeCache(data));
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to load products", TOAST_CFG);
      console.error("Fetch products failed", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  // ── CRUD — always bust cache & hard-refresh ────────────────────────────────
  const handleCreate = async (formData) => {
    setSaving(true);
    try {
      const response = await API.post(API_BASE, formData);
      toast.success(response.data.message || "Product created successfully", TOAST_CFG);
      setModalOpen(false);
      bustCache();
      await fetchProducts(true);
    } catch (error) {
      toast.error(error.response?.data?.message || "Error creating product", TOAST_CFG);
    } finally { setSaving(false); }
  };

  const handleUpdate = async (formData) => {
    setSaving(true);
    try {
      const response = await API.put(`${API_BASE}/${editTarget._id}`, formData);
      toast.success(response.data.message || "Product updated successfully", TOAST_CFG);
      setModalOpen(false);
      bustCache();
      await fetchProducts(true);
    } catch (error) {
      toast.error(error.response?.data?.message || "Error updating product", TOAST_CFG);
    } finally { setSaving(false); }
  };

  const handleDeleteConfirm = async () => {
    setConfirmOpen(false);
    if (!deleteTarget) return;
    try {
      const response = await API.delete(`${API_BASE}/${deleteTarget._id}`);
      toast.success(response.data.message || "Product deleted successfully", TOAST_CFG);
      bustCache();
      await fetchProducts(true);
    } catch (error) {
      toast.error(error.response?.data?.message || "Error deleting product", TOAST_CFG);
    } finally { setDeleteTarget(null); }
  };

  const handleSave = (data) => modalMode === "edit" ? handleUpdate(data) : handleCreate(data);
  const openAdd    = ()    => { setModalMode("add");  setEditTarget(null); setModalOpen(true); };
  const openEdit   = (row) => { setModalMode("edit"); setEditTarget(row);  setModalOpen(true); };
  const openDel    = (row) => { setDeleteTarget(row); setConfirmOpen(true); };

  const handleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("asc"); }
    setPage(1);
  };

  // ── Derived: filter → sort → paginate ─────────────────────────────────────
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products;
    return products.filter(p =>
      p.brand?.toLowerCase().includes(q)       ||
      p.itemCode?.toLowerCase().includes(q)    ||
      p.sectionName?.toLowerCase().includes(q) ||
      p.profileCode?.toLowerCase().includes(q) ||
      p.item?.toLowerCase().includes(q)
    );
  }, [products, search]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      let av = a[sortBy] ?? "";
      let bv = b[sortBy] ?? "";
      if (typeof av === "number") return sortDir === "asc" ? av - bv : bv - av;
      av = String(av).toLowerCase();
      bv = String(bv).toLowerCase();
      return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    });
    return arr;
  }, [filtered, sortBy, sortDir]);

  const totalPages = Math.ceil(sorted.length / PAGE_SIZE) || 1;
  const paginated  = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const cacheAge = cacheTs
    ? (() => {
        const sec = Math.round((Date.now() - cacheTs) / 1000);
        return sec < 60 ? `${sec}s ago` : `${Math.round(sec / 60)}m ago`;
      })()
    : null;

  return (
    <>
      <style>{GLOBAL_CSS}</style>

      <div
        className="min-h-screen bg-[#f4f4fa]"
        style={{ backgroundImage: "radial-gradient(ellipse 70% 30% at 60% -10%, rgba(124,109,250,0.07), transparent 60%)" }}
      >
        <div className="max-w-[1540px] mx-auto px-4 py-6 md:px-6 md:py-8">

          {/* ── PAGE HEADER ── */}
          <div className="flex items-start justify-between flex-wrap gap-y-3 mb-8 anim-fade-up">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <div className="w-1.5 h-6 rounded-full bg-gradient-to-b from-[#7c6dfa] to-[#40c0f0]" />
                <h1 className="text-[28px] font-bold text-[#1a1a2e] tracking-[1px]" style={RAJ}>Products</h1>
              </div>
              <p className="text-[12px] text-[#4a4a75] pl-5" style={MONO}>
                {products.length} section profile{products.length !== 1 ? "s" : ""} in catalog
                {cacheAge && <span className="ml-2 text-[#b8b8d4]">· cached {cacheAge}</span>}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => exportCSV(sorted)}
                disabled={loading || sorted.length === 0}
                className="inline-flex items-center gap-2 px-4 py-[9px] rounded-[8px] border border-[rgba(64,208,152,0.4)] text-[#40d098] text-[12px] font-bold hover:bg-[rgba(64,208,152,0.08)] transition-all disabled:opacity-40"
                style={RAJ}
                title="Export visible rows to CSV"
              >
                ↓ Export CSV
              </button>
              <button
                onClick={openAdd}
                className="inline-flex items-center gap-2 px-5 py-[10px] rounded-[8px] bg-[#7c6dfa] text-white text-[13px] font-bold tracking-[0.5px] hover:bg-[#9080ff] hover:-translate-y-px transition-all shadow-[0_4px_18px_rgba(124,109,250,0.35)]"
                style={RAJ}
              >
                <span className="text-[16px] leading-none">+</span> Add Product
              </button>
            </div>
          </div>

          {/* ── STATS STRIP ── */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 anim-fade-up" style={{ animationDelay: ".05s" }}>
            {[
              { label: "Total Products",     val: products.length,                                                      cls: "text-[#7c6dfa]" },
              { label: "Filtered / Showing", val: `${filtered.length} / ${paginated.length}`,                          cls: "text-[#40c0f0]" },
              { label: "Thickness Variants", val: products.reduce((s, p) => s + (p.profileThickness?.length || 0), 0), cls: "text-[#40d098]" },
              { label: "HSN Code",           val: products[0]?.hsnCode || "—",                                         cls: "text-[#f0c040]" },
            ].map((s, i) => (
              <div key={i} className="bg-[#f8f8ff] border border-[#d0d0e8] rounded-xl px-5 py-4 relative overflow-hidden shadow-sm">
                <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#b8b8d4] to-transparent" />
                <p className="text-[9px] tracking-[2px] uppercase text-[#4a4a75] mb-1" style={MONO}>{s.label}</p>
                <p className={`text-[22px] font-bold ${s.cls}`} style={RAJ}>{s.val}</p>
              </div>
            ))}
          </div>

          {/* ── TOOLBAR ── */}
          <div className="flex items-center gap-3 mb-4 flex-wrap anim-fade-up" style={{ animationDelay: ".1s" }}>
            {/* Search */}
            <div className="relative flex-1 min-w-[180px] max-w-[340px]">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4a4a75] text-[14px]">⌕</span>
              <input
                className={`${INPUT_CLS} pl-8`}
                style={MONO}
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                placeholder="Search brand, code, section…"
              />
              {search && (
                <button
                  onClick={() => { setSearch(""); setPage(1); }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4a4a75] hover:text-[#f05060] text-[14px] transition-colors"
                >✕</button>
              )}
            </div>

            {/* Group By */}
            <div className="flex items-center gap-2">
              <span className="text-[9.5px] tracking-[1.2px] uppercase text-[#4a4a75] whitespace-nowrap" style={MONO}>Group by</span>
              <select
                className={`${INPUT_CLS} min-w-[130px] cursor-pointer`}
                style={{ ...MONO, padding: "8px 10px" }}
                value={groupBy}
                onChange={e => { setGroupBy(e.target.value); setPage(1); }}
              >
                {GROUP_OPTIONS.map(o => <option key={o.val} value={o.val}>{o.label}</option>)}
              </select>
            </div>

            {/* Active pills */}
            <div className="flex items-center gap-2 flex-wrap">
              {sortBy && (
                <span className="text-[10px] bg-[rgba(124,109,250,0.1)] border border-[rgba(124,109,250,0.25)] text-[#7c6dfa] px-[9px] py-[3px] rounded-full" style={MONO}>
                  ↕ {sortBy} {sortDir === "asc" ? "↑" : "↓"}
                </span>
              )}
              {(search || groupBy) && (
                <button
                  onClick={() => { setSearch(""); setGroupBy(""); setSortBy("brand"); setSortDir("asc"); setPage(1); }}
                  className="text-[10px] text-[#f05060] hover:underline cursor-pointer"
                  style={MONO}
                >clear all</button>
              )}
            </div>

            <button
              onClick={() => fetchProducts(true)}
              disabled={loading}
              className="ml-auto inline-flex items-center gap-2 px-4 py-[8px] rounded-[6px] border border-[#b8b8d4] text-[#4a4a75] text-[11px] font-semibold hover:border-[#7c6dfa] hover:text-[#1a1a2e] transition-all disabled:opacity-40"
              style={RAJ}
            >
              {loading
                ? <span className="spin w-3 h-3 border-2 border-[#b8b8d4] border-t-[#7c6dfa] rounded-full inline-block" />
                : "↺"}
              Refresh
            </button>
          </div>

          {/* ── TABLE ── */}
          <div
            className="relative bg-[#f8f8ff] border border-[#d0d0e8] rounded-xl overflow-hidden shadow-sm anim-fade-up"
            style={{ animationDelay: ".13s" }}
          >
            <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#7c6dfa] via-[#40c0f0] to-[#f0c040]" />

            <div className="overflow-x-auto">
              <table className="w-full border-collapse min-w-[1000px]">
                <thead>
                  <tr className="bg-[#eeeef6] border-b border-[#d0d0e8]">
                    {SORT_COLS.map(col =>
                      col.col ? (
                        <SortableHeader
                          key={col.h}
                          label={col.h}
                          col={col.col}
                          sortBy={sortBy}
                          sortDir={sortDir}
                          onSort={handleSort}
                          align={col.a}
                        />
                      ) : (
                        <th
                          key={col.h}
                          className={`px-3 py-[11px] text-[9px] tracking-[1.2px] uppercase text-[#4a4a75] font-medium whitespace-nowrap text-${col.a}`}
                          style={MONO}
                        >{col.h}</th>
                      )
                    )}
                  </tr>
                </thead>

                <tbody>
                  {loading
                    ? Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
                    : paginated.length === 0
                    ? (
                      <tr>
                        <td colSpan={12} className="text-center py-16">
                          <div className="text-[40px] opacity-20 mb-3">{search ? "⌕" : "📦"}</div>
                          <p className="text-[14px] font-semibold text-[#1a1a2e] opacity-30 mb-1" style={RAJ}>
                            {search ? "No matches found" : "No products yet"}
                          </p>
                          <p className="text-[12px] text-[#4a4a75]" style={MONO}>
                            {search ? "Try a different search term" : "Click 'Add Product' to get started"}
                          </p>
                        </td>
                      </tr>
                    )
                    : paginated.map((p, i) => {
                        const globalIdx  = (page - 1) * PAGE_SIZE + i;
                        const showGroup  = groupBy && (i === 0 || paginated[i - 1][groupBy] !== p[groupBy]);
                        return (
                          <React.Fragment key={p._id}>
                            {showGroup && (
                              <tr className="bg-[rgba(124,109,250,0.05)] border-y border-[rgba(124,109,250,0.12)]">
                                <td colSpan={12} className="px-4 py-[6px]">
                                  <span className="text-[10px] font-bold tracking-[2px] uppercase text-[#7c6dfa]" style={MONO}>
                                    {p[groupBy] || "—"}
                                  </span>
                                </td>
                              </tr>
                            )}
                            <tr
                              className="row-enter border-b border-[rgba(208,208,232,0.7)] transition-colors hover:bg-[rgba(124,109,250,0.04)] group"
                              style={{ animationDelay: `${i * 0.03}s` }}
                            >
                              <td className="px-3 py-[10px] text-center text-[11px] text-[#4a4a75]" style={MONO}>{globalIdx + 1}</td>
                              <td className="px-3 py-[10px] text-center">
                                <img src={p.sectionImage} alt={p.sectionName}
                                  className="w-9 h-9 rounded-[5px] border border-[#d0d0e8] object-cover inline-block bg-[#eeeef6]"
                                  onError={e => { e.target.src = "https://placehold.co/36x36/eeeef6/999?text=?"; }} />
                              </td>
                              <td className="px-3 py-[10px]">
                                <span className="inline-block px-2 py-[2px] rounded-[4px] text-[10px] font-semibold tracking-[0.5px] bg-[rgba(240,192,64,0.1)] text-[#c89a00] border border-[rgba(240,192,64,0.3)]" style={RAJ}>
                                  {p.brand}
                                </span>
                              </td>
                              <td className="px-3 py-[10px] text-[11px] font-medium text-[#7c6dfa] break-words min-w-[100px] max-w-[160px]" style={MONO}>{p.itemCode}</td>
                              <td className="px-3 py-[10px] text-[11px] text-[#4a4a75] break-words min-w-[100px] max-w-[160px]" style={MONO}>{p.profileCode}</td>
                              <td className="px-3 py-[10px] min-w-[100px] max-w-[160px]">
                                <span className="text-[12px] font-semibold text-[#1a1a2e] break-words" style={SANS}>{p.sectionName}</span>
                              </td>
                              <td className="px-3 py-[10px] text-[11px] text-[#4a4a75] break-words min-w-[100px] max-w-[160px]" style={MONO}>{p.item}</td>
                              <td className="px-3 py-[10px] text-[11px] text-[#4a4a75]" style={MONO}>{p.hsnCode}</td>
                              <td className="px-3 py-[10px] text-center">
                                <span className="inline-block px-2 py-[1px] rounded-[3px] text-[9px] tracking-[1px] bg-[rgba(64,192,240,0.1)] text-[#1a9ec0] border border-[rgba(64,192,240,0.25)]" style={MONO}>
                                  {p.uom}
                                </span>
                              </td>
                              <td className="px-3 py-[10px]">
                                <ThicknessBadges variants={p.profileThickness || []} />
                              </td>
                              <td className="px-3 py-[10px] text-[11px] text-[#1a1a2e] text-right" style={MONO}>{p.lengthDefault}</td>
                              <td className="px-3 py-[10px] text-center">
                                <div className="flex items-center justify-center gap-2 opacity-60 group-hover:opacity-100 transition-opacity">
                                  <button
                                    onClick={() => openEdit(p)}
                                    className="inline-flex items-center px-3 py-[5px] rounded-[6px] border border-[rgba(124,109,250,0.35)] text-[#7c6dfa] text-[11px] font-semibold hover:bg-[rgba(124,109,250,0.1)] transition-all"
                                    style={RAJ}
                                  >Edit</button>
                                  <button
                                    onClick={() => openDel(p)}
                                    className="inline-flex items-center px-3 py-[5px] rounded-[6px] border border-[rgba(240,80,96,0.35)] text-[#f05060] text-[11px] font-semibold hover:bg-[rgba(240,80,96,0.1)] transition-all"
                                    style={RAJ}
                                  >Del</button>
                                </div>
                              </td>
                            </tr>
                          </React.Fragment>
                        );
                      })
                  }
                </tbody>

                {!loading && paginated.length > 0 && (
                  <tfoot>
                    <tr className="bg-[#eeeef6]" style={{ borderTop: "1px solid #b8b8d4" }}>
                      <td colSpan={9} className="px-3 py-[9px] text-[11px] font-bold text-[#4a4a75] text-right tracking-[1px]" style={RAJ}>
                        PAGE VARIANTS →
                      </td>
                      <td className="px-3 py-[9px] text-[11px] font-bold text-[#40d098]" style={MONO}>
                        {paginated.reduce((s, p) => s + (p.profileThickness?.length || 0), 0)} entries
                      </td>
                      <td colSpan={2} />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>

            {/* ── PAGINATION ── */}
            <Pagination
              page={page}
              pages={totalPages}
              total={sorted.length}
              perPage={PAGE_SIZE}
              onPage={setPage}
            />

            {/* Bottom info bar (only when no pagination) */}
            {totalPages <= 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-[#d0d0e8] bg-[rgba(240,240,252,0.85)]">
                <span className="text-[10px] text-[#4a4a75]" style={MONO}>
                  {loading ? "Loading…" : `${sorted.length} of ${products.length} products`}
                </span>
              </div>
            )}
          </div>

        </div>
      </div>

      {/* ── MODALS ── */}
      <ProductModal
        open={modalOpen}
        mode={modalMode}
        initial={editTarget}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
        saving={saving}
      />

      <ConfirmDialog
        open={confirmOpen}
        productName={deleteTarget?.sectionName || deleteTarget?.itemCode}
        onConfirm={handleDeleteConfirm}
        onCancel={() => { setConfirmOpen(false); setDeleteTarget(null); }}
      />
    </>
  );
}